import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Loader2, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AcceptOnboardingSupplier from "@/v2/api/supplier/AcceptOnboarding";

interface ApproveOnboardingButtonProps {
    id: string;
    isInside?: boolean;
}

function ApproveOnboardingButton({
    id,
    isInside = false,
}: ApproveOnboardingButtonProps) {
    const queryClient = useQueryClient();
    const [isOpen, setIsOpen] = useState(false);

    const mutation = useMutation({
        mutationFn: (id: string) => AcceptOnboardingSupplier({ id }),
        onError: (err) => {
            console.log({ err });
            toast.error("Oops Something went wrong", {
                description:
                    "While approving the supplier encountered some issue",
            });
            setIsOpen(false);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["onboard-suppliers"],
            });
            toast.success("Supplier Approved Successfully", {
                description:
                    "The supplier has been approved and can now proceed.",
            });
            setIsOpen(false);
        },
    });

    return (
        <Dialog open={isOpen} onOpenChange={(isOpen) => setIsOpen(isOpen)}>
            <DialogTrigger asChild>
                <Button
                    variant={isInside ? "default" : "outline"}
                    size="sm"
                    onClick={() => setIsOpen(true)}
                >
                    <CheckCircle className="h-4 w-4" />
                    {isInside && "Approve"}
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Approve Onboarding</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to approve this supplier's
                        onboarding? Click approve to confirm.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => setIsOpen(false)}
                        disabled={mutation.isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            mutation.mutate(id);
                        }}
                        disabled={mutation.isPending}
                    >
                        {mutation.isPending ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Approving...
                            </>
                        ) : (
                            "Approve"
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ApproveOnboardingButton;
